import {
  MitekHintsModel,
  MitekMessagesModel,
  MitekUpdateModel,
  MitekUpdateResultModel,
  MitekUpdateStatus,
  MitekDocType
} from '@models/mitek';
import { Subject, Observable } from 'rxjs';

export class MitekCapture {
  /**
   * sdk mitek
   */
  protected sdk: any;

  /**
   * timer per gestire il timeout
   */
  protected attemptTimer: any;

  /**
   * tipo di soggetto catturato
   */
  protected captureType: MitekDocType;

  /**
   * messaggi
   */
  private messages: MitekMessagesModel;

  /**
   * suggerimenti
   */
  private hints: MitekHintsModel;

  /**
   * subject per gli update
   */
  private update$: Subject<MitekUpdateModel>;

  constructor(
    sdk: any,
    options: {
      captureType: MitekDocType;
      messages: MitekMessagesModel;
      hints: MitekHintsModel;
    }
  ) {
    this.sdk = sdk;
    this.captureType = options.captureType;
    this.messages = options.messages || {};
    this.hints = options.hints || {};
  }

  /**
   * avvia la cattura
   */
  start(): Observable<MitekUpdateModel> {
    // may be needed to address new browser standards (explicit video play)
    document
      .querySelector<HTMLVideoElement>('div video[autoplay="true"]')
      .play();

    this.update$ = new Subject();

    this.sdk.on('CAMERA_DISPLAY_STARTED', () => {
      console.log('CAMERA_DISPLAY_STARTED');
      this.onCameraDisplayStarted();
    });

    this.sdk.on('FRAME_PROCESSING_FEEDBACK', result => {
      console.log('CAMERA_DISPLAY_STARTED', result);
      this.onFrameProcessingFeedback(result);
    });

    this.sdk.on('FRAME_PROCESSING_STARTED', () => {
      console.log('FRAME_PROCESSING_STARTED');
      this.onFrameProcessingStarted();
    });

    this.sdk.on('FRAME_CAPTURE_RESULT', result => {
      console.log('FRAME_CAPTURE_RESULT', result);
      this.onFrameCaptureResult(result);
    });

    this.sdk.on('SDK_ERROR', error => {
      console.error('SDK_ERROR', error);
      this.onSdkError(error);
    });

    this.sdk.cmd('CAPTURE_AND_PROCESS_FRAME', {
      mode: 'AUTO_CAPTURE',
      documentType: this.captureType,
      mitekSDKPath: './assets/scripts/mitek/mitekSDK',
      options: {
        qualityPercent: 80, // value between 50 to 100 recommended
        hintFrequencyMS: 1200, // how long the hint message displays (in MS) / note: 2400 recommended for screen readers
        hintAriaLive: 2, //screen reader setting for hint updates value (0=off / 1=polite / 2=assertive)
        guidePaddingLevel: 1, // size of the overlay guide  (1=large, 2=medium, 3=small) / note: 1 is recommended
        hintMessageSize: 2, // set the size of the hint message (1=small / 2=medium / 3=large)
        disableSmileDetection: false, // disable smile detection
        faceDetectionLevel: 1, // determines how strict face detection is (1=lenient, 2=moderate, 3=strict)
        videoContainerId: null, // (optionally) embed the video in a custom HTML container
        videoRecordingEnabled: false, // (optionally) record the video session / returned as a dataURL
        audioRecordingEnabled: false, // (optionally) also record audio / only applies if videoRecordingEnabled = true)
        videoQuality: 70,
        videoRecordingMessage: 'Recording'
      }
    });

    setTimeout(() => {
      this.emitUpdate('start');
    }, 10);

    return this.update$;
  }

  /**
   * gestisce l'evento di inizio acquisizione
   */
  private onCameraDisplayStarted(): void {
    this.emitUpdate('acquiring');
    this.showFirstHint();
  }

  /**
   * gestisce gli eventi di feedback della registrazione
   */
  private onFrameProcessingFeedback(res: any): void {
    const recentHint = res.key;

    if (this.captureType === 'SELFIE') {
      if (
        recentHint === 'MISNAP_SMILE' ||
        recentHint === 'MISNAP_STOP_SMILING' ||
        recentHint === 'MISNAP_READY_POSE'
      ) {
        this.updateFaceGuide('in');
      } else {
        this.updateFaceGuide('out');
      }
    }

    if (recentHint === 'MITEK_ERROR_FOUR_CORNER') {
      this.showPositioningHint();
    } else if (!!recentHint) {
      this.showCaptureHint(recentHint);
    }
  }

  /**
   * gestisce l'evento di inizio elaborazione
   */
  private onFrameProcessingStarted(): void {
    this.runAttemptTimer();
  }

  /**
   * gestisce l'evento di fine elaborazione
   */
  private onFrameCaptureResult(res: any): void {
    this.stopAttemptTimer();
    this.emitUpdate('success', res.response);
  }

  /**
   * gestisce l'evento di errore
   */
  private onSdkError(err: any): void {
    this.emitUpdateError(err);
  }

  /**
   * avvia il timer per il timeout
   */
  private runAttemptTimer(): void {
    this.attemptTimer = setTimeout(() => {
      console.log('timeout');

      this.stopAttemptTimer();
      this.sdk.cmd('SDK_STOP');
      this.emitUpdateError('timeout');
    }, 25000);
  }

  /**
   * interrompe il timer per il timeout
   */
  private stopAttemptTimer(): void {
    clearTimeout(this.attemptTimer);
    this.attemptTimer = undefined;
  }

  /**
   * aggiorna la guida per inquadrare il volto
   */
  private updateFaceGuide(detection: 'in' | 'out'): void {
    const guide = document.getElementsByClassName('integrator SELFIE')[0];

    if (!guide) {
      return;
    }

    if (detection === 'in') {
      guide.classList.add('FACE_IN_GUIDE');
    } else {
      guide.classList.remove('FACE_IN_GUIDE');
    }
  }

  /**
   * mostra il primo suggerimento
   */
  private showFirstHint(): void {
    this.showHint(this.getFirstMessage());
  }

  /**
   * mostra il messaggio di warning di posizionamento
   */
  private showPositioningHint(): void {
    this.showHint(this.getPositioningMessage());
  }

  /**
   * mostra suggerimento per la cattura
   */
  private showCaptureHint(code: string): void {
    this.showHint(this.getHint(code));
  }

  /**
   * mostra il suggerimento
   */
  private showHint(hint: string): void {
    if (!!hint) {
      this.sdk.cmd('SHOW_HINT', hint);
    }
  }

  /**
   * restituisce il primo messaggio
   */
  private getFirstMessage(): string {
    return (
      this.messages[this.captureType] &&
      this.messages[this.captureType].firstMessage
    );
  }

  /**
   * restituisce messaggio sul posizionamento
   */
  private getPositioningMessage(): string {
    return (
      this.messages[this.captureType] &&
      this.messages[this.captureType].fourCornerMessage
    );
  }

  /**
   * restituisce il suggerimento legato al codice
   */
  private getHint(code: string): string {
    return this.hints[code];
  }

  /**
   * emette update
   */
  private emitUpdate(
    status: MitekUpdateStatus,
    result?: MitekUpdateResultModel
  ): void {
    if (!(this.update$ instanceof Subject)) {
      this.update$ = new Subject();
    }

    if (!!result) {
      this.update$.next({ status, result });
      this.update$.complete();
    } else {
      this.update$.next({ status });
    }
  }

  /**
   * emette update con errore
   */
  private emitUpdateError(error: any): void {
    if (!(this.update$ instanceof Subject)) {
      this.update$ = new Subject();
    }

    this.update$.error(error);
  }
}
